import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { formatWhatsappDisplay } from 'lib/helpers/masks';
import CustomizedTable, {
  ITableBody,
} from './consultant-request-details-table';
import { RequestApi } from 'lib/api/requests';
import CustomizedSnackbars from 'lib/components/SnackBar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { getFileDisplayName } from 'modules/requests/utils/file-display-name';
import {
  BaseBreadcrumbs,
  BaseCardBox,
  FieldRow,
  Button,
  FileInput,
  ErrorMsg,
  FakeButton,
  FileUploadCard,
  FileUploadCardText,
  SectionHeader,
  SectionLabel,
  ManualLink,
  SelectFileButton,
  HintText,
  UploadedFileRow,
  RemoveFileButton,
} from './style';
import { FileDto, ReportType } from 'lib/api/file.protocols';
import { FileApi } from 'lib/api/file';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { IRequestResponseDTO } from 'lib/api/requests.protocols';
import { useSnackbar } from 'notistack';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';

import { IElement } from 'lib/common/types';
import { brand } from 'themes/brand';

export interface ISnackBarSettings {
  show: boolean;
  message?: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
}

const VALID_RESULTS = [
  'INAPTO(A)',
  'APTO(A) COM RESTRIÇÕES',
  'APTO(A)',
  'INFERIOR',
  'MÉDIO INFERIOR',
  'MÉDIO',
];

const normalizeForComparison = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos: É→E, Ç→C, Ã→A
    .replace(/:/g, '/') // sistema de arquivo pode substituir / por :
    .trim()
    .toUpperCase();

const findValidResult = (extracted: string): string | null =>
  VALID_RESULTS.find(
    (r) => normalizeForComparison(r) === normalizeForComparison(extracted),
  ) ?? null;

const extractResultFromFilename = (filename: string): string => {
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  const parts = nameWithoutExt.split(' - ');
  return parts.length >= 4 ? parts[parts.length - 1].trim() : '';
};

const tableHead = ['Nome', 'Descrição', 'Nome', 'Descrição'];

const ConsultantRequestDetailsPage = ({
  goToAllRequests,
  id,
}: {
  goToAllRequests: () => void;
  id: string;
}) => {
  const [requestData, setRequestData] = useState<IRequestResponseDTO>();

  const [files, setFiles] = useState<Array<{ name: string; data: any }>>([]);
  const [reportFiles, setReportFiles] = useState<FileDto[] | null>(null);
  const [snackBarSettings, setSnackBarSettings] = useState<ISnackBarSettings>({
    show: false,
  });

  const [requestChanges, setRequestChanges] = useState({
    testSubmissionDate: null,
    resultSentToClientDate: null,
    result: '',
    status: '',
    consultantNote: '',
  });

  const [showSaveButton, setShowSaveButton] = useState(true);
  const [inLoading, setInLoading] = useState(false);
  const [fileFail, setFileFail] = useState(false);
  const [tableBody, setTableBody] = useState<ITableBody[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // Auto mode state
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [mainFileUploaded, setMainFileUploaded] = useState<any[]>([]);
  const [mainFileName, setMainFileName] = useState('');
  const [autoResult, setAutoResult] = useState('');
  const [mainFileFail, setMainFileFail] = useState(false);
  const [mainFilePatternError, setMainFilePatternError] = useState('');
  const [showMainFileLoading, setShowMainFileLoading] = useState(false);
  const [compFilesUploaded, setCompFilesUploaded] = useState<
    Array<{ name: string; data: any }>
  >([]);
  const [showCompFilesLoading, setShowCompFilesLoading] = useState(false);
  const [compFileFail, setCompFileFail] = useState(false);

  const getReportFiles = async () => {
    try {
      const reportFiles = await FileApi.searchByRequestId(id);
      setReportFiles(reportFiles);
    } catch (e) {
      console.error('Erro ao buscar arquivo', e);
    }
  };

  useEffect(() => {
    getReportFiles();
  }, [requestData]);

  const resetSnackBar = () => {
    setTimeout(() => {
      setSnackBarSettings({ show: false });
    }, 3000);
  };

  const fetchRequest = async (id: string) => {
    try {
      const request = await RequestApi.getRequest(id);

      setRequestData(request);
      setRequestChanges((prev: any) => ({
        ...prev,
        testSubmissionDate: request.requestManagement.testSubmissionDate
          ? request.requestManagement.testSubmissionDate
          : null,
        resultSentToClientDate: request.requestManagement.resultSentToClientDate
          ? request.requestManagement.resultSentToClientDate
          : null,
        status: request.status ? request.status : '',
        result: request.requestManagement.result
          ? request.requestManagement.result
          : '',
        consultantNote: request.requestManagement.consultantNote
          ? request.requestManagement.consultantNote
          : '',
      }));
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK') {
        return setSnackBarSettings({
          show: true,
          message:
            'Servidor instável, aguarde alguns segundos e recarregue a página por favor',
          severity: 'error',
        });
      }
      console.error(error);
    }
  };

  useEffect(() => {
    fetchRequest(id);
  }, []);

  const fakeButtonBgColor = (variant: string | undefined) => {
    switch (variant) {
      case 'em andamento':
        return brand.status.warning.bg;
      case 'cancelado':
        return brand.status.error.bg;
      case 'correção':
        return brand.status.attention.bg;
      case 'congelado':
        return brand.status.neutral.bg;
      case 'finalizado':
        return brand.status.success.bg;
      case 'reanálise':
        return brand.status.info.bg;
    }
  };

  const getReportUrl = async (
    azureFileName: string,
    reportUser: IElement | undefined,
  ) => {
    try {
      const reportType = reportUser
        ? ReportType.CONSULTANT_REPORT
        : ReportType.ASSESSMENT_REPORT;

      const url = await FileApi.getReportUrl({ azureFileName, reportType });
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', url);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Erro ao gerar url do arquivo', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (!requestData || !Object.keys(requestData).length) {
      setTableBody([]);
      return;
    }

    const body = [
      {
        label1: 'Solicitante:',
        value1: requestData?.analyst.name,
        label2: 'Data solicitação:',
        value2: dayjs(requestData?.requestDate).format('DD/MM/YYYY'),
      },
      {
        label1: 'Empresa:',
        value1: requestData?.company.name,
        label2: 'Data de envio dos links:',
        value2: requestData?.requestManagement.linkSentDate
          ? dayjs(requestData?.requestManagement.linkSentDate).format(
              'DD/MM/YYYY',
            )
          : '',
      },
      {
        label1: 'E-mail do solicitante:',
        value1: requestData?.analyst.email ? requestData?.analyst.email : '',
        label2: 'Data de envio ao cliente:',
        value2: requestData?.requestManagement.resultSentToClientDate
          ? dayjs(requestData?.requestManagement.resultSentToClientDate).format(
              'DD/MM/YYYY',
            )
          : '',
      },
      {
        label1: 'Instrumento a ser enviado:',
        value1: requestData?.instrument.name,
        label2: 'Centro de custo:',
        value2: requestData?.costCenter,
      },
      {
        label1: 'Nome completo:',
        value1: requestData?.candidate.name,
        label2: 'Unidade organizacional:',
        value2: requestData?.sector.name,
      },
      {
        label1: 'CPF:',
        value1: requestData?.candidate.cpf,
        label2: 'Observações:',
        value2: requestData?.additionalNote,
      },
      {
        label1: 'E-mail do avaliado:',
        value1: requestData?.candidate.email,
        label2: 'Status:',
        value2: (
          <FakeButton
            variant={fakeButtonBgColor(
              requestData &&
                requestData.status &&
                requestData?.status.toLowerCase(),
            )}
          >
            {requestData && requestData.status}
          </FakeButton>
        ),
      },
      {
        label1: 'Whatsapp:',
        value1: formatWhatsappDisplay(requestData?.candidate.whatsapp),
        label2: 'Arquivos:',
        value2: reportFiles ? (
          <React.Fragment>
            {reportFiles &&
              reportFiles.map((file, index) => {
                return (
                  <a
                    key={index}
                    style={{
                      display: 'block',
                      textDecoration: 'none',
                      color: brand.primary,
                      cursor: 'pointer',
                    }}
                    onClick={() => getReportUrl(file.azureFileName, file.user)}
                  >
                    {getFileDisplayName(file, index)}
                  </a>
                );
              })}
          </React.Fragment>
        ) : (
          ''
        ),
      },
      {
        label1: 'Cargo:',
        value1: requestData?.jobTitle.name,
        label2: 'Resultado:',
        value2: requestData?.requestManagement.result
          ? requestData?.requestManagement.result.toLowerCase()
          : '',
      },
      {
        label1: 'Origem da solicitação:',
        value1: requestData?.gupyApplicationId ? 'Sistema Gupy' : 'Sistema MS',
        label2: 'Observação do(a) consultor(a) MS:',
        value2: requestData?.requestManagement.consultantNote,
      },
    ];
    setTableBody(body);
  }, [reportFiles, requestData]);

  // Manual mode: handles file uploads
  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const newFiles = Array.from(e.target.files);
    const formData = new FormData();
    newFiles.forEach((f) => formData.append('files', f));

    try {
      setShowSaveButton(false);
      setFileFail(false);
      const response = await RequestApi.retrieveFilesLinks(formData);
      const newUploaded = newFiles.map((f, i) => ({
        name: f.name,
        data: response[i],
      }));
      setFiles((prev) => [...prev, ...newUploaded]);
      setShowSaveButton(true);
    } catch (err: any) {
      setShowSaveButton(false);
      setFileFail(true);
      if (err.message === 'Network Error') {
        setSnackBarSettings({
          show: true,
          message: 'Cada arquivo deve ter no máximo 3mb',
          severity: 'error',
        });
      } else {
        setSnackBarSettings({
          show: true,
          message: err.message,
          severity: 'error',
        });
      }
      resetSnackBar();
    }
  };

  // Auto mode: handles the main file (extracts result from filename)
  const handleMainFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset previous state
    setMainFileName('');
    setAutoResult('');
    setMainFileUploaded([]);
    setMainFileFail(false);
    setMainFilePatternError('');

    const extracted = extractResultFromFilename(file.name);
    const validResult = findValidResult(extracted);

    if (!validResult) {
      setMainFilePatternError(
        extracted
          ? `Resultado "${extracted}" não é válido. O arquivo deve seguir o padrão: Tipo - Bateria/Teste - Nome - Resultado.`
          : 'Nome do arquivo não segue o padrão esperado. Use: Tipo - Bateria/Teste - Nome - Resultado.',
      );
      return;
    }

    setAutoResult(validResult);
    setMainFileName(file.name);

    const formData = new FormData();
    formData.append('files', file);

    try {
      setShowMainFileLoading(true);
      const response = await RequestApi.retrieveFilesLinks(formData);
      setMainFileUploaded(response);
    } catch (err: any) {
      setMainFileFail(true);
      if (err.message === 'Network Error') {
        setSnackBarSettings({
          show: true,
          message: 'Cada arquivo deve ter no máximo 3mb',
          severity: 'error',
        });
      } else {
        setSnackBarSettings({
          show: true,
          message: err.message,
          severity: 'error',
        });
      }
      resetSnackBar();
    } finally {
      setShowMainFileLoading(false);
    }
  };

  // Auto mode: handles complementary files (appends to existing list)
  const handleCompFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const newFiles = Array.from(e.target.files);
    const formData = new FormData();
    newFiles.forEach((f) => formData.append('files', f));

    try {
      setShowCompFilesLoading(true);
      setCompFileFail(false);
      const response = await RequestApi.retrieveFilesLinks(formData);
      const newUploaded = newFiles.map((f, i) => ({
        name: f.name,
        data: response[i],
      }));
      setCompFilesUploaded((prev) => [...prev, ...newUploaded]);
    } catch (err: any) {
      setCompFileFail(true);
      if (err.message === 'Network Error') {
        setSnackBarSettings({
          show: true,
          message: 'Cada arquivo deve ter no máximo 3mb',
          severity: 'error',
        });
      } else {
        setSnackBarSettings({
          show: true,
          message: err.message,
          severity: 'error',
        });
      }
      resetSnackBar();
    } finally {
      setShowCompFilesLoading(false);
    }
  };

  const removeMainFile = () => {
    setMainFileName('');
    setAutoResult('');
    setMainFileUploaded([]);
    setMainFilePatternError('');
    setMainFileFail(false);
    setCompFilesUploaded([]);
    setCompFileFail(false);
  };

  const removeCompFile = (index: number) => {
    setCompFilesUploaded((prev) => prev.filter((_, i) => i !== index));
  };

  const removeManualFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetAutoMode = () => {
    setIsAutoMode(true);
    setAutoResult('');
    setMainFileUploaded([]);
    setMainFileName('');
    setMainFileFail(false);
    setMainFilePatternError('');
    setCompFilesUploaded([]);
    setCompFileFail(false);
    setFiles([]);
  };

  const handleSaveChanges = async () => {
    const allUploadedFiles = isAutoMode
      ? [...mainFileUploaded, ...compFilesUploaded.map((f) => f.data)]
      : files.map((f) => f.data);

    const resultToSave = isAutoMode ? autoResult : requestChanges.result;

    const needsFile =
      requestChanges?.resultSentToClientDate !== null ||
      requestChanges?.status?.toUpperCase() === 'FINALIZADO';

    if (
      needsFile &&
      allUploadedFiles.length === 0 &&
      reportFiles?.length === 0
    ) {
      setSnackBarSettings({
        show: true,
        message: 'Favor escolher ao menos um arquivo',
        severity: 'error',
      });
      resetSnackBar();
      return;
    }

    if (
      isAutoMode &&
      mainFileUploaded.length > 0 &&
      !VALID_RESULTS.includes(autoResult)
    ) {
      setSnackBarSettings({
        show: true,
        message:
          'Resultado extraído do arquivo não é válido. Verifique o nome do arquivo.',
        severity: 'error',
      });
      resetSnackBar();
      return;
    }

    setInLoading(true);

    try {
      if (
        requestChanges.status &&
        requestChanges.status !== requestData?.status
      ) {
        const newStatus = {
          status: requestChanges.status,
        };
        await RequestApi.editRequest(newStatus, id);
      }

      const requestManagementData = {
        testSubmissionDate: requestChanges.testSubmissionDate,
        resultSentToClientDate: requestChanges.resultSentToClientDate,
        result: resultToSave,
        consultantNote: requestChanges.consultantNote,
      };

      await RequestApi.editRequestManagement(requestManagementData, id);

      if (allUploadedFiles.length) {
        await FileApi.createFile({
          files: allUploadedFiles,
          companyId: requestData?.company?.id as string,
          sectorsIds: [requestData?.sector?.id as string],
          requestId: id,
        }).then(() => getReportFiles());

        if (isAutoMode) {
          setMainFileUploaded([]);
          setCompFilesUploaded([]);
          setMainFileName('');
          setAutoResult('');
          setMainFilePatternError('');
        } else {
          setFiles([]);
        }
      }

      fetchRequest(id);
      setSnackBarSettings({
        show: true,
        message: 'Requisição editada com sucesso!',
        severity: 'success',
      });
      resetSnackBar();
      setInLoading(false);
    } catch (err: any) {
      setInLoading(false);
      if (err.code === 'ERR_NETWORK') {
        return setSnackBarSettings({
          show: true,
          message:
            'Servidor instável, aguarde alguns segundos e recarregue a página por favor',
          severity: 'error',
        });
      }

      setSnackBarSettings({
        show: true,
        message: err.message,
        severity: 'error',
      });
      resetSnackBar();
    }
  };

  const isFinalizadoWithDate =
    requestChanges?.resultSentToClientDate !== null &&
    requestChanges?.status?.toUpperCase() === 'FINALIZADO';

  const autoReady =
    !isAutoMode ||
    (mainFileUploaded.length > 0 && VALID_RESULTS.includes(autoResult));

  const isSaveDisabled =
    inLoading ||
    (!isAutoMode && !showSaveButton && !fileFail) ||
    (isFinalizadoWithDate && !autoReady && reportFiles?.length === 0);

  return (
    <React.Fragment>
      <BaseBreadcrumbs aria-label="breadcrumb">
        <Link onClick={goToAllRequests}>minhas solicitações</Link>
        <Typography color="text.primary">detalhes da solicitação</Typography>
      </BaseBreadcrumbs>

      <BaseCardBox>
        <h3>Detalhes da solicitação</h3>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, mb: 1 }}
        >
          Ao definir <strong>Data do envio ao cliente</strong> e o status como{' '}
          <strong>Finalizado</strong>, você poderá anexar o arquivo principal —
          o resultado será preenchido automaticamente a partir do nome do
          arquivo.
        </Typography>

        <FieldRow style={{ marginTop: '25px' }}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data de recebimento do avaliado"
                format="DD/MM/YYYY"
                value={
                  requestChanges.testSubmissionDate &&
                  dayjs(requestChanges.testSubmissionDate)
                }
                onChange={(event) => {
                  setRequestChanges((prev: any) => ({
                    ...prev,
                    testSubmissionDate: event,
                  }));
                }}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data do envio ao cliente"
                format="DD/MM/YYYY"
                value={
                  requestChanges.resultSentToClientDate &&
                  dayjs(requestChanges.resultSentToClientDate)
                }
                onChange={(event: any) =>
                  setRequestChanges((prev: any) => ({
                    ...prev,
                    resultSentToClientDate: event,
                  }))
                }
              />
            </LocalizationProvider>
          </FormControl>
        </FieldRow>

        <FieldRow>
          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              label="Status"
              onChange={(event) =>
                setRequestChanges((prev: any) => ({
                  ...prev,
                  status: event.target.value,
                }))
              }
              value={requestChanges.status?.toLowerCase() ?? ''}
            >
              <MenuItem value="">
                <i>Válido</i>
              </MenuItem>
              <MenuItem value="em andamento">Em Andamento</MenuItem>
              <MenuItem value="correção">Correção</MenuItem>
              <MenuItem value="cancelado">Cancelado</MenuItem>
              <MenuItem value="congelado">Congelado</MenuItem>
              <MenuItem value="finalizado">Finalizado</MenuItem>
              <MenuItem value="reanálise">Reanálise</MenuItem>
            </Select>
          </FormControl>

          {requestData?.status !== 'CONGELADO' &&
            requestData?.status !== 'CANCELADO' && (
              <FormControl fullWidth>
                {isFinalizadoWithDate &&
                reportFiles?.length === 0 &&
                isAutoMode ? (
                  <>
                    <InputLabel id="result-label" shrink>
                      Resultado
                    </InputLabel>
                    <Select
                      labelId="result-label"
                      label="Resultado"
                      value={autoResult || ''}
                      disabled
                      displayEmpty
                      renderValue={() =>
                        autoResult
                          ? autoResult.toLowerCase()
                          : 'Será preenchido pelo arquivo'
                      }
                    >
                      <MenuItem value={autoResult || ''}>
                        {autoResult
                          ? autoResult.toLowerCase()
                          : 'Será preenchido pelo arquivo'}
                      </MenuItem>
                    </Select>
                  </>
                ) : reportFiles && reportFiles.length > 0 ? (
                  <>
                    <InputLabel id="result-label" shrink>
                      Resultado
                    </InputLabel>
                    <Select
                      labelId="result-label"
                      label="Resultado"
                      value={requestChanges.result ?? ''}
                      disabled
                      displayEmpty
                      renderValue={() =>
                        requestChanges.result
                          ? requestChanges.result.toLowerCase()
                          : 'sem resultado'
                      }
                    >
                      <MenuItem value={requestChanges.result ?? ''}>
                        {requestChanges.result
                          ? requestChanges.result.toLowerCase()
                          : 'sem resultado'}
                      </MenuItem>
                    </Select>
                  </>
                ) : (
                  <>
                    <InputLabel id="result-label">Resultado</InputLabel>
                    <Select
                      labelId="result-label"
                      label="Resultado"
                      onChange={(event) =>
                        setRequestChanges((prev: any) => ({
                          ...prev,
                          result: event.target.value,
                        }))
                      }
                      value={requestChanges.result ?? ''}
                    >
                      <MenuItem value="">sem resultado</MenuItem>
                      <MenuItem value="INAPTO(A)">inapto(a)</MenuItem>
                      <MenuItem value="APTO(A)">apto(a)</MenuItem>
                      <MenuItem value="APTO(A) COM RESTRIÇÕES">
                        apto(a) com restrições
                      </MenuItem>
                      <MenuItem value="INFERIOR">inferior</MenuItem>
                      <MenuItem value="MÉDIO INFERIOR">médio inferior</MenuItem>
                      <MenuItem value="MÉDIO">médio</MenuItem>
                    </Select>
                  </>
                )}
              </FormControl>
            )}
        </FieldRow>

        <FieldRow>
          <FormControl fullWidth>
            <TextField
              fullWidth
              multiline
              inputProps={{ maxLength: 500 }}
              name="consultantNote"
              label="Observação do(a) consultor(a)"
              type="textarea"
              value={requestChanges.consultantNote}
              onChange={(event: any) =>
                setRequestChanges((prev: any) => ({
                  ...prev,
                  consultantNote: event.target.value,
                }))
              }
            />
          </FormControl>
        </FieldRow>

        {reportFiles?.length === 0 && isFinalizadoWithDate && (
          <FieldRow style={{ flexDirection: 'column', gap: '12px' }}>
            {isAutoMode ? (
              <React.Fragment key="auto-mode">
                <SectionHeader>
                  <SectionLabel>Arquivo Principal</SectionLabel>
                  <ManualLink onClick={() => setIsAutoMode(false)}>
                    <EditIcon fontSize="small" /> Inserir resultado manualmente
                  </ManualLink>
                </SectionHeader>

                <FileUploadCard>
                  <FileUploadCardText>
                    <p>Anexe o arquivo principal — o nome define o resultado</p>
                    <p>
                      Padrão: Tipo - Bateria/Teste - Nome - Resultado (ex.:
                      EstruturalMS - BOL - MARIA EDUARDA DIAS - APTO(A).pdf)
                    </p>
                  </FileUploadCardText>
                  {!mainFileName && (
                    <SelectFileButton>
                      <UploadFileIcon fontSize="small" /> Selecionar arquivo
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleMainFile}
                      />
                    </SelectFileButton>
                  )}
                </FileUploadCard>

                {showMainFileLoading && (
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                  </Box>
                )}
                {mainFilePatternError && (
                  <ErrorMsg>{mainFilePatternError}</ErrorMsg>
                )}
                {mainFileFail && (
                  <ErrorMsg>
                    Erro ao carregar o arquivo. Favor tentar novamente.
                  </ErrorMsg>
                )}
                {mainFileName && !mainFileFail && (
                  <UploadedFileRow>
                    <span title={mainFileName}>
                      ✓ {mainFileName} — Resultado: {autoResult}
                    </span>
                    <RemoveFileButton
                      type="button"
                      title="Remover arquivo"
                      onClick={removeMainFile}
                    >
                      <CloseIcon fontSize="small" />
                    </RemoveFileButton>
                  </UploadedFileRow>
                )}

                {mainFileUploaded.length > 0 && (
                  <>
                    <SectionLabel style={{ marginTop: '8px' }}>
                      Arquivos Complementares (opcional)
                    </SectionLabel>

                    {compFilesUploaded.map((f, i) => (
                      <UploadedFileRow key={i}>
                        <span title={f.name}>{f.name}</span>
                        <RemoveFileButton
                          type="button"
                          title="Remover arquivo"
                          onClick={() => removeCompFile(i)}
                        >
                          <CloseIcon fontSize="small" />
                        </RemoveFileButton>
                      </UploadedFileRow>
                    ))}

                    <SelectFileButton style={{ alignSelf: 'flex-start' }}>
                      <UploadFileIcon fontSize="small" />
                      {compFilesUploaded.length > 0
                        ? 'Adicionar mais arquivos'
                        : 'Selecionar arquivos'}
                      <input type="file" multiple onChange={handleCompFiles} />
                    </SelectFileButton>

                    {showCompFilesLoading && (
                      <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                      </Box>
                    )}
                    {compFileFail && (
                      <ErrorMsg>Arquivo(s) inválido(s).</ErrorMsg>
                    )}
                  </>
                )}
              </React.Fragment>
            ) : (
              <React.Fragment key="manual-mode">
                <SectionHeader>
                  <SectionLabel>Arquivo</SectionLabel>
                  <ManualLink onClick={resetAutoMode}>
                    Voltar ao modo automático
                  </ManualLink>
                </SectionHeader>

                {files.map((f, i) => (
                  <UploadedFileRow key={i}>
                    <span title={f.name}>{f.name}</span>
                    <RemoveFileButton
                      type="button"
                      title="Remover arquivo"
                      onClick={() => removeManualFile(i)}
                    >
                      <CloseIcon fontSize="small" />
                    </RemoveFileButton>
                  </UploadedFileRow>
                ))}

                <SelectFileButton style={{ alignSelf: 'flex-start' }}>
                  <UploadFileIcon fontSize="small" />
                  {files.length > 0
                    ? 'Adicionar mais arquivos'
                    : 'Selecionar arquivos'}
                  <input type="file" multiple onChange={handleFiles} />
                </SelectFileButton>

                {!showSaveButton && !fileFail && (
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                  </Box>
                )}
                {fileFail && (
                  <ErrorMsg>
                    Arquivo(s) inválido(s), favor selecionar outro(s)
                    arquivo(s).
                  </ErrorMsg>
                )}
              </React.Fragment>
            )}
          </FieldRow>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mt: '25px' }}>
          <Button
            variant="contained"
            onClick={() => handleSaveChanges()}
            disabled={isSaveDisabled}
            startIcon={
              inLoading ? <CircularProgress color="inherit" /> : undefined
            }
          >
            Salvar solicitação
          </Button>
          {isFinalizadoWithDate &&
            isAutoMode &&
            !autoReady &&
            reportFiles?.length === 0 && (
              <HintText>
                Anexe o arquivo principal e defina o resultado para salvar.
              </HintText>
            )}
        </Box>
      </BaseCardBox>

      <CustomizedTable tableHead={tableHead} tableBody={tableBody} />

      {snackBarSettings.show && (
        <CustomizedSnackbars
          message={snackBarSettings.message}
          severity={snackBarSettings.severity}
        />
      )}
    </React.Fragment>
  );
};

export { ConsultantRequestDetailsPage };
